"use client";

import { motion } from "framer-motion";
import BackpackInstructions from "./BackpackInstructions";
import PackingInformation from "./PackingInformation";
import PackingLayers from "./PackingLayers";
import gearImgBackground from "../../assets/images/expedition-gear/backgroundImage.png";

/* ================= ANIMATIONS ================= */

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

export default function EssentialGears() {
  return (
    <main className="bg-[#0e1418] text-white ">

      {/* ================= HERO ================= */}
<section
  className="
     relative
    h-[85vh] min-h-[600px]
    w-full
    flex items-center justify-center
    px-6 md:px-12
    bg-cover bg-center
    overflow-x-hidden
  "
  style={{ backgroundImage: `url(${gearImgBackground})` }}
>
  {/* DARK GRADIENT OVERLAY */}
  <div className="absolute inset-0 bg-gradient-to-b from-gray-300 via-white/60 to-gray-400 z-0" />

  <motion.div
    initial="hidden"
    animate="visible"
    variants={{
      visible: {
        transition: { staggerChildren: 0.15 },
      },
    }}
    className="relative z-10 max-w-5xl mx-auto text-center space-y-10"
  >
    <motion.p
      variants={fadeUp}
      className="tracking-[0.45em] text-xs text-neutral-700 uppercase"
    >
      Gear Intelligence
    </motion.p>

    <motion.h1
      variants={fadeUp}
      className="text-4xl md:text-6xl font-extrabold leading-tight text-[#192c37]"
    >
      Expedition Gear
      <span className="block text-[#192c37]/80 font-light">
        packed with intent.
      </span>
    </motion.h1>

    <motion.p
      variants={fadeUp}
      className="max-w-2xl mx-auto text-lg text-[#192c37]/80"
    >
      In high-altitude environments, packing is strategy.
      Balance, access, and weight placement determine success.
    </motion.p>
  </motion.div>
</section>

<PackingInformation/>
<PackingLayers/>
      {/* ================= PACKING SYSTEM ================= */}
      <section className="relative py-28 px-6 md:px-12 
      bg-cover bg-center
  "
  style={{ backgroundImage: `url(${gearImgBackground})` }}
>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* LEFT — NARRATIVE */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-12"
          >
            <motion.h2
              variants={fadeUp}
              className="text-3xl text-[#112535] md:text-4xl font-semibold"
            >
              The Layered Packing System
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="text-neutral-900 max-w-xl"
            >
              A backpack isn’t filled randomly. It is engineered in layers.
              Each zone serves a specific function optimizing balance,
              access, and energy expenditure during long days on the mountain.
            </motion.p>

            {/* LAYERS */}
            <div className="space-y-10 pt-6">
              {[
                {
                  n: "01",
                  title: "Bottom Layer",
                  desc:
                    "Reserved for items not needed during the day. Sleep systems and shelter stay protected and out of the access path.",
                },
                {
                  n: "02",
                  title: "Middle Layer",
                  desc:
                    "The structural core. Heaviest items sit closest to your spine to stabilize movement and preserve centre of gravity.",
                },
                {
                  n: "03",
                  title: "Top Layer",
                  desc:
                    "Rapid-access essentials. Insulation, hydration, navigation, and emergency gear must be reachable instantly.",
                },
              ].map((l) => (
                <motion.div
                  key={l.n}
                  variants={fadeUp}
                  className="flex gap-6"
                >
                  <div className="text-4xl font-bold text-[#213646]">
                    {l.n}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#112535]">
                      {l.title}
                    </h3>
                    <p className="text-neutral-800 mt-2">
                      {l.desc}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — DIAGRAM */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-120px" }}
          className="relative overflow-hidden"
        >
          <div className="absolute -inset-10 bg-white/5 rounded-3xl blur-2xl" />
          <BackpackInstructions />
        </motion.div>

        </div>
      </section>
    </main>
  );
}
